import { useState, useRef, useEffect, FunctionComponent } from "react";
import Link from "next/link";
import {
  IBlog,
  INavItemFields,
  INavLinkFields,
  INavSectionFields,
  IPageFields,
} from "@/types/generated/contentful";
import { BlogPostTile } from "../blogLayout/blogPostTitle";

type Props = {
  closeNav: Function;
  content: INavItemFields;
};

const VerticalDrawer: FunctionComponent<Props> = ({ closeNav, content }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div className="mx-auto w-full max-w-md md:max-w-full">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-x-1"
      >
        <span className="text-base">{content.title}</span>
        <div
          className={`transition-all duration-500 ease-in-out ${isOpen ? "-rotate-180" : ""}`}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.2836 6.84729C12.9996 5.97796 12.3802 4.66663 11.2536 4.66663H4.7469C3.62023 4.66663 3.00157 5.97796 3.71757 6.84729L6.97157 10.7986C7.09665 10.9506 7.25382 11.0729 7.43179 11.1569C7.60975 11.2409 7.80411 11.2845 8.0009 11.2845C8.19769 11.2845 8.39205 11.2409 8.57001 11.1569C8.74798 11.0729 8.90515 10.9506 9.03023 10.7986L12.2836 6.84729Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </button>

      <div
        style={{
          height: `${height}px`,
        }}
        className="-mx-4 overflow-hidden transition-all duration-300 ease-in-out"
      >
        <div ref={contentRef} className="p-4 md:pb-0">
          <div className="flex flex-col md:flex-row">
            {content &&
              content.sections.map((section, index) => {
                const sectionFields = section.fields as INavSectionFields;
                const isBlog = (section.fields as INavSectionFields).items
                  ? false
                  : true;

                if (isBlog) {
                  return (
                    <div
                      key={index}
                      className="hidden max-w-lg md:flex"
                      tabIndex={-1}
                      onBlur={() => {
                        if (index === content.sections.length - 1) {
                          setIsOpen(false);
                        }
                      }}
                    >
                      <BlogPostTile
                        content={section as IBlog}
                        nav={true}
                        tabIndex={isOpen ? 0 : -1}
                      />
                    </div>
                  );
                }
                return (
                  <div key={index} className="flex flex-col gap-2">
                    <span className="text-base font-bold text-primary">
                      {sectionFields.title}
                    </span>
                    <ul className="flex flex-col px-4">
                      {sectionFields.items.map((item) => {
                        const itemFields = item.fields as INavLinkFields;
                        const isPage = (itemFields.page.fields as IPageFields)
                          .content
                          ? true
                          : false;
                        const pageFields = itemFields.page
                          .fields as IPageFields;

                        return (
                          <li
                            key={item.sys.id}
                            className="pointer -translate-x-4 rounded-md p-4 hover:bg-gray-100 md:pr-0 last:md:pb-0"
                          >
                            <Link
                              href={
                                isPage
                                  ? `/${pageFields.slug}`
                                  : `/insights?tag=${pageFields.slug}`
                              }
                              className=""
                              tabIndex={isOpen ? 0 : -1}
                              onClick={() => {
                                closeNav();
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="text-base font-bold">
                                  {itemFields.title}
                                </span>
                                {itemFields.description}
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerticalDrawer;
